// hooks/useSelectionData.js
import { useMemo } from 'react';
import { useExcerpt } from './excerpts/queries';

/**
 * Custom hook to create a selection object based on selected excerpt or manual selection
 * 
 * This hook provides consistent selection handling for both transcript and survey views,
 * ensuring that only appropriate selections are applied to the correct content.
 * 
 * @param {string|number} selectedExcerptId - ID of the selected excerpt, if any
 * @param {object} select - Manual selection object with start/end, if any
 * @param {string|number} codeableId - ID of the codeable item (transcript or survey answer)
 * @param {string} codeableType - Type of the codeable item ('Transcript' or 'SurveyAnswer')
 * @returns {object|null} Selection data object or null if no valid selection
 */
export function useSelectionData(selectedExcerptId, select, codeableId, codeableType) {
  // Fetch the selected excerpt data
  const { data: selectedExcerpt } = useExcerpt(selectedExcerptId);
  
  // Create and return the selection object
  return useMemo(() => {
    // Check for valid selected excerpt that matches this content
    if (selectedExcerptId && selectedExcerpt && 
        selectedExcerpt.transcript_id === codeableId &&
        selectedExcerpt.codeable_type === codeableType) {
      return {
        selectedExcerptId,
        selectionStart: selectedExcerpt.start,
        selectionEnd: selectedExcerpt.end
      };
    } 
    // Check for valid manual selection that matches this content
    else if (select && 
             (!select.codeableId || select.codeableId === codeableId) &&
             (!select.codeableType || select.codeableType === codeableType)) {
      return {
        selectedExcerptId, // Pass along the selectedExcerptId even with manual selection
        selectionStart: select.start,
        selectionEnd: select.end
      };
    }
    return null;
  }, [selectedExcerptId, selectedExcerpt, select, codeableId, codeableType]);
}

export default useSelectionData;
